"use client";

import React, { useRef, useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import "./BlogsMarquee.css";

const blogs = [];


export default function BlogsMarquee() {
  const trackRef = useRef(null);
  const viewportRef = useRef(null);
  const [blogsList, setBlogsList] = useState([]);

  // Dragging and animation states
  const isDraggingRef = useRef(false);
  const hasDraggedRef = useRef(false);
  const startXRef = useRef(0);
  const lastXRef = useRef(0);
  const dragStartXRef = useRef(0);
  const targetXRef = useRef(0);
  const autoScrollXRef = useRef(0);
  const velocityRef = useRef(0);
  const animationRef = useRef(null);
  const isHoveredRef = useRef(false);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const apiUrl = (process.env.NEXT_PUBLIC_API_URL || '').replace(/\/api\/?$/, '').replace(/\/$/, '') || "";
        const res = await fetch(`${apiUrl}/api/blog/active`);
        if (res.ok) {
          const json = await res.json();
          if (json.success && json.data && json.data.length > 0) {
            setBlogsList(json.data);
          }
        }
      } catch (err) {
        console.warn("Error fetching dynamic blogs for marquee (using static fallback):", err.message || err);
      }
    };
    fetchBlogs();
  }, []);

  const mappedDynamic = (blogsList || []).map(blog => {
    let rawImg = blog.image || blog.images?.[0] || "/assets/img/blog-home-2.jpeg";
    // Safe check for objects (multer file uploads or database metadata objects)
    if (rawImg && typeof rawImg === "object") {
      rawImg = rawImg.path || rawImg.url || rawImg.secure_url || "/assets/img/blog-home-2.jpeg";
    }
    
    let imgSrc = String(rawImg || "/assets/img/blog-home-2.jpeg");

    if (imgSrc && !imgSrc.startsWith("http") && !imgSrc.startsWith("blob:") && !imgSrc.startsWith("data:") && !imgSrc.startsWith("/")) {
      imgSrc = `${(process.env.NEXT_PUBLIC_API_URL || "").replace(/\/api\/?$/, "")}/${imgSrc}`;
    }

    const dateStr = blog.createdAt
      ? new Date(blog.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
      : "Feb 7, 2024";

    return {
      id: blog._id,
      title: blog.title,
      image: imgSrc,
      date: dateStr,
      link: `/blog/${blog.slug}`
    };
  });

  const displayBlogs = mappedDynamic;
  const isStatic = displayBlogs.length < 4;

  // Repeat items to fill at least 12 slots for smooth marquee loop (only if not static)
  let repeatedBlogs = [];
  if (displayBlogs.length > 0) {
    if (isStatic) {
      repeatedBlogs = displayBlogs;
    } else {
      while (repeatedBlogs.length < 12) {
        repeatedBlogs = [...repeatedBlogs, ...displayBlogs];
      }
    }
  }
  const allBlogs = repeatedBlogs;

  useEffect(() => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    if (!viewport || !track) return;

    if (isStatic) {
      track.style.transform = "none";
      viewport.style.cursor = "default";
      return;
    }

    const onMouseDown = (e) => {
      if (e.button !== 0) return; // Only track left-click
      isDraggingRef.current = true;
      hasDraggedRef.current = false;
      startXRef.current = e.clientX;
      lastXRef.current = e.clientX;
      dragStartXRef.current = autoScrollXRef.current;
      targetXRef.current = autoScrollXRef.current;
      velocityRef.current = 0;
      viewport.style.cursor = "grabbing";
    };

    const onMouseMove = (e) => {
      if (!isDraggingRef.current) return;
      const deltaX = e.clientX - startXRef.current;
      const currentDelta = e.clientX - lastXRef.current;
      lastXRef.current = e.clientX;

      if (Math.abs(deltaX) > 5) {
        hasDraggedRef.current = true;
      }
      
      targetXRef.current = dragStartXRef.current - deltaX;
      // Filter velocity with a simple low-pass moving average
      velocityRef.current = velocityRef.current * 0.6 + (-currentDelta) * 0.4;
    };

    const onMouseUp = () => {
      if (isDraggingRef.current) {
        isDraggingRef.current = false;
        viewport.style.cursor = "grab";
      }
    };

    // Touch support
    const onTouchStart = (e) => {
      isDraggingRef.current = true;
      hasDraggedRef.current = false;
      const clientX = e.touches[0].clientX;
      startXRef.current = clientX;
      lastXRef.current = clientX;
      dragStartXRef.current = autoScrollXRef.current;
      targetXRef.current = autoScrollXRef.current;
      velocityRef.current = 0;
    };

    const onTouchMove = (e) => {
      if (!isDraggingRef.current) return;
      const clientX = e.touches[0].clientX;
      const deltaX = clientX - startXRef.current;
      const currentDelta = clientX - lastXRef.current;
      lastXRef.current = clientX;

      if (Math.abs(deltaX) > 5) {
        hasDraggedRef.current = true;
      }
      
      targetXRef.current = dragStartXRef.current - deltaX;
      velocityRef.current = velocityRef.current * 0.6 + (-currentDelta) * 0.4;
    };

    const onTouchEnd = () => {
      isDraggingRef.current = false;
    };

    // Click handler during/after drag prevention
    const onClickCapture = (e) => {
      if (hasDraggedRef.current) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const onMouseEnter = () => {
      isHoveredRef.current = true;
    };

    const onMouseLeave = () => {
      isHoveredRef.current = false;
      if (isDraggingRef.current) {
        isDraggingRef.current = false;
        viewport.style.cursor = "grab";
      }
    };

    // Attach listeners
    viewport.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    
    viewport.addEventListener("touchstart", onTouchStart, { passive: true });
    viewport.addEventListener("touchmove", onTouchMove, { passive: true });
    viewport.addEventListener("touchend", onTouchEnd);
    
    viewport.addEventListener("click", onClickCapture, true);
    viewport.addEventListener("mouseenter", onMouseEnter);
    viewport.addEventListener("mouseleave", onMouseLeave);
    let cachedHalfWidth = 0;
    const updateDimensions = () => {
      if (track) {
        cachedHalfWidth = track.scrollWidth / 2;
      }
    };
    updateDimensions();
    window.addEventListener("resize", updateDimensions, { passive: true });

    // Animation Loop
    const animate = () => {
      const halfWidth = cachedHalfWidth;

      if (isDraggingRef.current) {
        // Lerp position towards target during dragging for smooth tracking
        autoScrollXRef.current += (targetXRef.current - autoScrollXRef.current) * 0.22;
      } else {
        // Apply velocity decay (inertia) or default auto-scroll
        if (Math.abs(velocityRef.current) > 0.15) {
          autoScrollXRef.current += velocityRef.current;
          velocityRef.current *= 0.93; // deceleration rate
          targetXRef.current = autoScrollXRef.current;
        } else {
          velocityRef.current = 0;
          if (!isHoveredRef.current) {
            autoScrollXRef.current += 0.65; // normal speed auto scroll
            targetXRef.current = autoScrollXRef.current;
          }
        }
      }

      // Loop point wrapping (glitch-proof coordinate shifting)
      if (halfWidth > 0) {
        while (autoScrollXRef.current < 0) {
          autoScrollXRef.current += halfWidth;
          targetXRef.current += halfWidth;
          dragStartXRef.current += halfWidth;
        }
        while (autoScrollXRef.current >= halfWidth) {
          autoScrollXRef.current -= halfWidth;
          targetXRef.current -= halfWidth;
          dragStartXRef.current -= halfWidth;
        }
      }

      track.style.transform = `translateX(-${autoScrollXRef.current}px)`;
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      viewport.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      
      viewport.removeEventListener("touchstart", onTouchStart);
      viewport.removeEventListener("touchmove", onTouchMove);
      viewport.removeEventListener("touchend", onTouchEnd);
      
      viewport.removeEventListener("click", onClickCapture, true);
      viewport.removeEventListener("mouseenter", onMouseEnter);
      viewport.removeEventListener("mouseleave", onMouseLeave);
      window.removeEventListener("resize", updateDimensions);
      
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [allBlogs.length, isStatic]);

  return (
    <section className="blogs-marquee-section">
      {/* Header */}
      <div className="blogs-marquee-header">
        <div className="blogs-marquee-title-group">
          <span className="blogs-eyebrow">BLOGS</span>
          <h2 className="blogs-main-title">
            Our Latest Insights & Articles
          </h2>
        </div>
      </div>

      {/* Scrolling marquee track */}
      <div
        className={`blogs-marquee-viewport ${isStatic ? "is-static" : ""}`}
        ref={viewportRef}
      >
        <div className="blogs-marquee-track" ref={trackRef}>
          {allBlogs.map((blog, idx) => (
            <div key={idx} className="blog-card-outer">
              <Link href={blog.link} className="blog-card-marquee" draggable={false}>
                {/* Image */}
                <div className="blog-card-img-wrap">
                  <Image
                    src={blog.image}
                    alt={blog.title}
                    fill
                    className="blog-card-img"
                    sizes="340px"
                    draggable={false}
                  />
                </div>

                {/* Title */}
                <div className="blog-card-body">
                  <span className="blog-card-date">{blog.date}</span>
                  <p className="blog-card-title">{blog.title}</p>
                  <span className="blog-card-cta">Read More ↗</span>
                </div>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
